
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AvatarUpload } from "./AvatarUpload";

interface ExtendedUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone?: string;
  linkedin_url?: string;
  location_city?: string;
  location_state?: string;
  location_country?: string;
  timezone?: string;
  bio?: string;
  years_experience?: number;
  specializations?: string[];
  work_hours_start?: string;
  work_hours_end?: string;
  preferred_communication?: string;
}

interface ProfileHeaderProps {
  userProfile: ExtendedUserProfile;
  onAvatarUpdate: (url: string) => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  userProfile,
  onAvatarUpdate,
}) => {
  const fullName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
  const location = [userProfile.location_city, userProfile.location_state, userProfile.location_country]
    .filter(Boolean)
    .join(', ');

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-6">
          <AvatarUpload
            currentAvatarUrl={userProfile.avatar_url}
            firstName={userProfile.first_name || ""}
            lastName={userProfile.last_name || ""}
            onAvatarUpdate={onAvatarUpdate}
          />
          
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{fullName || "Set your name"}</h2>
            <p className="text-muted-foreground text-lg">{userProfile.email}</p>
            
            <div className="flex items-center gap-4 mt-3">
              {location && (
                <span className="text-sm text-muted-foreground">{location}</span>
              )}
              {userProfile.years_experience && (
                <Badge variant="outline">
                  {userProfile.years_experience} years experience
                </Badge>
              )}
              {userProfile.timezone && (
                <Badge variant="outline">
                  {userProfile.timezone}
                </Badge>
              )}
            </div>
            
            {userProfile.bio && (
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {userProfile.bio}
              </p>
            )}
            
            {userProfile.specializations && userProfile.specializations.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {userProfile.specializations.map((spec, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {spec}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
